/**
 * Shared bulk component (gap 1 PR 2, D-18 item 7). OPEN-213 reuses it; keep it free of domain
 * logic.
 *
 * The preview table of a bulk price change: an optional checkbox column, the caller's columns,
 * the row status, and each row's messages under it. ERROR rows are highlighted. Above the table,
 * one counts line: "N new · N changes · N unchanged · N errors". Nothing here writes.
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';

export interface PreviewColumn<R> {
  readonly key: string;
  readonly header: string;
  readonly cell: (row: R) => React.ReactNode;
}

/** Row status labels. A caller with more statuses spreads this and adds its own. */
export const PRICE_STATUS_LABELS: Readonly<Record<string, string>> = {
  ADD: 'New',
  CHANGE: 'Change',
  UNCHANGED: 'No change',
  ERROR: 'Error',
};

const STATUS_COLORS: Readonly<Record<string, string>> = {
  ADD: 'var(--color-success)',
  CHANGE: 'var(--color-text-primary)',
  UNCHANGED: 'var(--color-text-secondary)',
  ERROR: 'var(--color-danger)',
};

function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** "N new · N changes · N unchanged · N errors", counted from the rows. */
export function previewCountsLine(rows: readonly { status: string }[]): string {
  const count = (s: string): number => rows.filter((r) => r.status === s).length;
  return [
    `${String(count('ADD'))} new`,
    plural(count('CHANGE'), 'change', 'changes'),
    `${String(count('UNCHANGED'))} unchanged`,
    plural(count('ERROR'), 'error', 'errors'),
  ].join(' · ');
}

export function PriceChangePreview<R extends { status: string; messages: readonly string[] }>(p: {
  rows: readonly R[];
  columns: readonly PreviewColumn<R>[];
  selectable?: { isSelected(r: R): boolean; toggle(r: R): void };
}): React.JSX.Element {
  const { rows, columns, selectable } = p;
  const width = columns.length + 1 + (selectable === undefined ? 0 : 1);
  return (
    <div>
      <p data-testid="preview-counts" style={{ marginBottom: '10px' }}>
        {previewCountsLine(rows)}
      </p>
      <div style={{ maxHeight: '420px', overflow: 'auto', marginBottom: '12px' }}>
        <Table>
          <TableHeader>
            <TableRow>
              {selectable !== undefined && (
                <TableHead>
                  <span className="sr-only">Select</span>
                </TableHead>
              )}
              {columns.map((c) => (
                <TableHead key={c.key}>{c.header}</TableHead>
              ))}
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => {
              const isError = r.status === 'ERROR';
              const color = STATUS_COLORS[r.status] ?? 'var(--color-text-primary)';
              return (
                <React.Fragment key={i}>
                  <TableRow
                    data-status={r.status}
                    style={
                      isError
                        ? {
                            backgroundColor:
                              'color-mix(in srgb, var(--color-danger) 8%, transparent)',
                          }
                        : undefined
                    }
                  >
                    {selectable !== undefined && (
                      <TableCell>
                        <input
                          type="checkbox"
                          aria-label={`Select row ${String(i + 1)}`}
                          checked={selectable.isSelected(r)}
                          onChange={(): void => selectable.toggle(r)}
                        />
                      </TableCell>
                    )}
                    {columns.map((c) => (
                      <TableCell key={c.key}>{c.cell(r)}</TableCell>
                    ))}
                    <TableCell style={{ color, fontWeight: 600 }}>
                      {PRICE_STATUS_LABELS[r.status] ?? r.status}
                    </TableCell>
                  </TableRow>
                  {r.messages.length > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={width}
                        style={{
                          fontSize: '13px',
                          paddingTop: 0,
                          color: isError ? STATUS_COLORS.ERROR : 'var(--color-text-secondary)',
                        }}
                      >
                        {r.messages.map((m) => (
                          <div key={m}>{m}</div>
                        ))}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
